/**
 * EnvelopeContext - Global state management for Envelope Challenge
 * Provides real-time sync between Envelope Challenge and Dashboard
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, getToken } from '@/utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const EnvelopeContext = createContext(null);

export function EnvelopeProvider({ children }) {
  // Envelope Challenge State
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [todayEnvelope, setTodayEnvelope] = useState(null);
  const [myProgress, setMyProgress] = useState({
    totalSaved: 0,
    currentStreak: 0,
    vaultAmount: 0,
    todayStatus: 'pending', // pending, saved, skipped
    totalWithdrawn: 0,
    hasPendingCatchup: false
  });
  
  // Challenge details
  const [challengeGoal, setChallengeGoal] = useState(0);
  const [challengeDuration, setChallengeDuration] = useState(0);
  
  // Guap Board State (leaderboard)
  const [guapBoard, setGuapBoard] = useState([]);
  
  // Dashboard sync state
  const [envelopeSavingsToday, setEnvelopeSavingsToday] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  
  // WebSocket connection
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Load initial envelope data
  const loadEnvelopeData = useCallback(async () => {
    try {
      const response = await apiGet('/envelope-v2/my-challenges');
      if (response.ok) {
        const data = await response.json();
        setActiveChallenges(data.challenges || []);
        
        // Find the most relevant challenge (active one)
        const activeChallenge = data.challenges?.find(c => c.status === 'active');
        if (activeChallenge) {
          // Set challenge goal and duration
          setChallengeGoal(activeChallenge.total_goal || 0);
          setChallengeDuration(activeChallenge.duration || 0);
          
          // Load detailed challenge data
          const detailResponse = await apiGet(`/envelope-v2/challenges/${activeChallenge.id}`);
          if (detailResponse.ok) {
            const detail = await detailResponse.json();
            
            // Update challenge goal from detailed data
            if (detail.challenge) {
              setChallengeGoal(detail.challenge.total_goal || 0);
              setChallengeDuration(detail.challenge.duration || 0);
            }
            
            // Update today's envelope
            const today = detail.today_envelope;
            if (today) {
              setTodayEnvelope({
                id: today.id,
                amount: today.amount,
                status: today.status,
                day: today.day
              });
            }
            
            // Update my progress
            const progress = detail.my_progress;
            if (progress) {
              setMyProgress({
                totalSaved: progress.total_saved || 0,
                currentStreak: progress.current_streak || 0,
                vaultAmount: progress.vault_amount || 0,
                todayStatus: progress.today_status || 'pending',
                totalWithdrawn: progress.total_withdrawn || 0,
                hasPendingCatchup: progress.has_pending_catchup || false
              });
              
              // If saved today, track the amount for dashboard deduction
              if (progress.today_status === 'saved' && today) {
                setEnvelopeSavingsToday(today.amount);
              }
            }
            
            // Update Guap Board with goal info
            if (detail.guap_board) {
              const boardWithGoals = detail.guap_board.map(p => ({
                ...p,
                goal: detail.challenge?.total_goal || 0
              }));
              setGuapBoard(boardWithGoals);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load envelope data:', error);
    }
  }, []);

  // Connect to WebSocket for real-time updates
  const connectWebSocket = useCallback((challengeId) => {
    if (!challengeId) return;
    
    const token = getToken();
    if (!token) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    const wsUrl = `${WS_URL}/api/envelope-v2/ws/${challengeId}?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('Envelope WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('Envelope WebSocket closed');
        // Reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket(challengeId);
        }, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('Envelope WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    const { type, payload } = data;
    
    switch (type) {
      case 'envelope_saved':
        // Someone saved an envelope
        setGuapBoard(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(p => p.user_id === payload.user_id);
          if (idx >= 0) {
            updated[idx] = {
              ...updated[idx],
              total_saved: payload.total_saved,
              current_streak: payload.current_streak
            };
          }
          return updated.sort((a, b) => (b.current_streak - a.current_streak) || (b.total_saved - a.total_saved));
        });
        
        // If it's the current user, update local state
        if (payload.is_self) {
          setMyProgress(prev => ({
            ...prev,
            totalSaved: payload.total_saved,
            currentStreak: payload.current_streak,
            todayStatus: 'saved'
          }));
          setEnvelopeSavingsToday(payload.amount);
          setTodayEnvelope(prev => prev ? { ...prev, status: 'saved' } : null);
        }
        
        setLastUpdate(Date.now());
        break;
        
      case 'envelope_skipped':
        if (payload.is_self) {
          setMyProgress(prev => ({
            ...prev,
            vaultAmount: payload.vault_amount,
            todayStatus: 'skipped'
          }));
          setTodayEnvelope(prev => prev ? { ...prev, status: 'skipped' } : null);
        }
        setLastUpdate(Date.now());
        break;
        
      case 'guap_board_update':
        setGuapBoard(payload.board || []);
        setLastUpdate(Date.now());
        break;
        
      case 'challenge_completed':
        loadEnvelopeData();
        break;
        
      default:
        break;
    }
  }, [loadEnvelopeData]);

  // Mark today's envelope as saved (called from EnvelopeChallenge)
  const markEnvelopeSaved = useCallback((amount, newStreak, totalSaved) => {
    setMyProgress(prev => ({
      ...prev,
      totalSaved: totalSaved,
      currentStreak: newStreak,
      todayStatus: 'saved'
    }));
    setEnvelopeSavingsToday(amount);
    setTodayEnvelope(prev => prev ? { ...prev, status: 'saved' } : null);
    setLastUpdate(Date.now());
  }, []);

  // Mark today's envelope as skipped (sent to vault)
  const markEnvelopeSkipped = useCallback((vaultAmount) => {
    setMyProgress(prev => ({
      ...prev,
      vaultAmount: vaultAmount,
      todayStatus: 'skipped'
    }));
    setTodayEnvelope(prev => prev ? { ...prev, status: 'skipped' } : null);
    setLastUpdate(Date.now());
  }, []);

  // Refresh all data
  const refreshData = useCallback(() => {
    loadEnvelopeData();
  }, [loadEnvelopeData]);

  // Load data on mount
  useEffect(() => {
    loadEnvelopeData();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [loadEnvelopeData]);

  // Connect WebSocket when we have an active challenge
  useEffect(() => {
    const activeChallenge = activeChallenges.find(c => c.status === 'active');
    if (activeChallenge) {
      connectWebSocket(activeChallenge.id);
    }
  }, [activeChallenges, connectWebSocket]);

  const value = {
    // State
    activeChallenges,
    todayEnvelope,
    myProgress,
    guapBoard,
    envelopeSavingsToday,
    lastUpdate,
    challengeGoal,
    challengeDuration,
    
    // Actions
    markEnvelopeSaved,
    markEnvelopeSkipped,
    refreshData,
    loadEnvelopeData,
    
    // Computed
    hasActiveChallenge: activeChallenges.some(c => c.status === 'active'),
    todayAmount: todayEnvelope?.amount || 0,
    canSaveToday: todayEnvelope?.status === 'pending'
  };

  return (
    <EnvelopeContext.Provider value={value}>
      {children}
    </EnvelopeContext.Provider>
  );
}

export function useEnvelope() {
  const context = useContext(EnvelopeContext);
  if (!context) {
    throw new Error('useEnvelope must be used within an EnvelopeProvider');
  }
  return context;
}

export default EnvelopeContext;
